import { useEffect, useRef, useState } from "react";

interface ProgramItem {
  id: string;
  title: string;
  description?: string;
  category: string;
  start_time: string | null;
  end_time: string | null;
  location?: string | null;
  location_lat?: number | null;
  location_lng?: number | null;
}

interface TripMapProps {
  programItems: ProgramItem[];
  tripDestination?: string;
}

// Kategorie → Hex-Farbe für den Pin-Kreis
const CATEGORY_COLORS: Record<string, string> = {
  Reisen:       "#3b82f6", // blue-500
  Übernachtung: "#eab308", // yellow-500
  Verpflegung:  "#10b981", // emerald-500
  Aktivitäten:  "#f97316", // orange-500
  Workshops:    "#ec4899", // pink-500
  Sonstiges:    "#6b7280", // gray-500
};

// Kategorie → Emoji für Filter-Chips und Bottom Sheet
const CATEGORY_EMOJIS: Record<string, string> = {
  Reisen:       "🚌",
  Übernachtung: "🏨",
  Verpflegung:  "🍽",
  Aktivitäten:  "🧗",
  Workshops:    "🎨",
  Sonstiges:    "📌",
};

function formatTime(dateStr: string): string {
  return new Date(dateStr).toLocaleTimeString("de-CH", { hour: "2-digit", minute: "2-digit" });
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString("de-CH", {
    weekday: "short",
    day: "numeric",
    month: "short",
  });
}

// Erstellt ein farbiges, nummeriertes Pin-Element als HTML
function createPinElement(color: string, number: number): HTMLElement {
  const div = document.createElement("div");
  div.style.cssText = `
    width: 32px;
    height: 32px;
    border-radius: 50% 50% 50% 0;
    transform: rotate(-45deg);
    background-color: ${color};
    border: 2px solid white;
    box-shadow: 0 2px 6px rgba(0,0,0,0.4);
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
  `;
  const label = document.createElement("span");
  label.style.cssText = `
    transform: rotate(45deg);
    color: white;
    font-size: 11px;
    font-weight: 700;
    font-family: sans-serif;
    line-height: 1;
  `;
  label.textContent = String(number);
  div.appendChild(label);
  return div;
}

export default function TripMap({ programItems, tripDestination }: TripMapProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markersRef = useRef<{ marker: google.maps.Marker; item: ProgramItem }[]>([]);

  const [selectedItem, setSelectedItem] = useState<ProgramItem | null>(null);
  const [activeCategories, setActiveCategories] = useState<Set<string>>(new Set());

  // Nur Items mit Koordinaten
  const mappableItems = programItems.filter(
    (item) => item.location_lat != null && item.location_lng != null
  );

  // Alle vorhandenen Kategorien
  const categories = Array.from(new Set(mappableItems.map((i) => i.category)));

  // Filter-Chip toggle
  const toggleCategory = (cat: string) => {
    setActiveCategories((prev) => {
      const next = new Set(prev);
      if (next.has(cat)) {
        next.delete(cat);
      } else {
        next.add(cat);
      }
      return next;
    });
  };

  const isFiltered = activeCategories.size > 0;
  const visibleItems = isFiltered
    ? mappableItems.filter((i) => activeCategories.has(i.category))
    : mappableItems;

  // Map initialisieren
  useEffect(() => {
    if (!mapRef.current || !window.google) return;

    const defaultCenter = { lat: 47.3769, lng: 8.5417 }; // Zürich als Fallback

    const center =
      mappableItems.length > 0
        ? {
            lat:
              mappableItems.reduce((sum, i) => sum + (i.location_lat ?? 0), 0) /
              mappableItems.length,
            lng:
              mappableItems.reduce((sum, i) => sum + (i.location_lng ?? 0), 0) /
              mappableItems.length,
          }
        : defaultCenter;

    mapInstanceRef.current = new google.maps.Map(mapRef.current, {
      center,
      zoom: mappableItems.length > 1 ? 10 : 13,
      disableDefaultUI: false,
      zoomControl: true,
      streetViewControl: false,
      mapTypeControl: false,
      fullscreenControl: false,
      styles: [
        { elementType: "geometry", stylers: [{ color: "#1a2035" }] },
        { elementType: "labels.text.stroke", stylers: [{ color: "#0d1117" }] },
        { elementType: "labels.text.fill", stylers: [{ color: "#94a3b8" }] },
        {
          featureType: "administrative.locality",
          elementType: "labels.text.fill",
          stylers: [{ color: "#cbd5e1" }],
        },
        {
          featureType: "poi",
          elementType: "labels.text.fill",
          stylers: [{ color: "#94a3b8" }],
        },
        {
          featureType: "poi.park",
          elementType: "geometry",
          stylers: [{ color: "#162032" }],
        },
        {
          featureType: "road",
          elementType: "geometry",
          stylers: [{ color: "#253046" }],
        },
        {
          featureType: "road",
          elementType: "geometry.stroke",
          stylers: [{ color: "#1a2035" }],
        },
        {
          featureType: "road.highway",
          elementType: "geometry",
          stylers: [{ color: "#2c3b56" }],
        },
        {
          featureType: "transit",
          elementType: "geometry",
          stylers: [{ color: "#253046" }],
        },
        {
          featureType: "water",
          elementType: "geometry",
          stylers: [{ color: "#0d1117" }],
        },
        {
          featureType: "water",
          elementType: "labels.text.fill",
          stylers: [{ color: "#475569" }],
        },
      ],
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Marker aktualisieren wenn sichtbare Items sich ändern
  useEffect(() => {
    if (!mapInstanceRef.current || !window.google) return;

    // Bestehende Marker entfernen
    markersRef.current.forEach(({ marker }) => marker.setMap(null));
    markersRef.current = [];

    // Neue Marker erstellen
    visibleItems.forEach((item, index) => {
      if (item.location_lat == null || item.location_lng == null) return;

      const color = CATEGORY_COLORS[item.category] ?? "#6b7280";
      const pinEl = createPinElement(color, index + 1);

      const marker = new google.maps.Marker({
        position: { lat: item.location_lat, lng: item.location_lng },
        map: mapInstanceRef.current!,
        icon: {
          url:
            "data:image/svg+xml;charset=UTF-8," +
            encodeURIComponent(`
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="40" viewBox="0 0 32 40">
                <path d="M16 0 C7.2 0 0 7.2 0 16 C0 24 16 40 16 40 C16 40 32 24 32 16 C32 7.2 24.8 0 16 0Z"
                  fill="${color}" stroke="white" stroke-width="2"/>
                <text x="16" y="20" text-anchor="middle" dominant-baseline="middle"
                  font-family="sans-serif" font-size="13" font-weight="700" fill="white">
                  ${index + 1}
                </text>
              </svg>
            `),
          scaledSize: new google.maps.Size(32, 40),
          anchor: new google.maps.Point(16, 40),
        },
      });

      marker.addListener("click", () => {
        setSelectedItem(item);
      });

      markersRef.current.push({ marker, item });
    });

    // Karte auf sichtbare Marker zentrieren
    if (visibleItems.length > 1) {
      const bounds = new google.maps.LatLngBounds();
      visibleItems.forEach((item) => {
        if (item.location_lat != null && item.location_lng != null) {
          bounds.extend({ lat: item.location_lat, lng: item.location_lng });
        }
      });
      mapInstanceRef.current.fitBounds(bounds, 60);
    } else if (visibleItems.length === 1 && visibleItems[0].location_lat) {
      mapInstanceRef.current.setCenter({
        lat: visibleItems[0].location_lat!,
        lng: visibleItems[0].location_lng!,
      });
      mapInstanceRef.current.setZoom(14);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleItems.length, activeCategories]);

  const noCoordinates = mappableItems.length === 0;

  return (
    <div className="flex flex-col" style={{ height: "calc(100vh - 13rem)" }}>
      {/* Filter-Chips */}
      {categories.length > 1 && (
        <div className="flex flex-wrap gap-2 px-4 py-3">
          {categories.map((cat) => {
            const active = activeCategories.has(cat);
            return (
              <button
                key={cat}
                onClick={() => toggleCategory(cat)}
                className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                  active
                    ? "bg-sky-500/20 text-sky-400 border border-sky-500/30"
                    : "bg-[#1e2639] text-slate-400 hover:text-slate-200"
                }`}
              >
                {CATEGORY_EMOJIS[cat] ?? "📌"} {cat}
                {active && <span className="opacity-50 ml-0.5">✕</span>}
              </button>
            );
          })}
          {isFiltered && (
            <button
              onClick={() => setActiveCategories(new Set())}
              className="px-3 py-1 rounded-full text-xs font-medium bg-[#1e2639] text-slate-500 hover:text-slate-300 transition-colors"
            >
              Alle anzeigen
            </button>
          )}
        </div>
      )}

      {/* Karte oder Empty State */}
      {noCoordinates ? (
        <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
          <p className="text-4xl mb-3">🗺</p>
          <p className="text-slate-400 text-sm mb-1">Keine Orte im Programm hinterlegt.</p>
          <p className="text-slate-600 text-xs">
            Füge beim Bearbeiten eines Programmpunkts einen Ort hinzu.
          </p>
        </div>
      ) : (
        <div ref={mapRef} className="flex-1 w-full" />
      )}

      {/* Bottom Sheet – Programmpunkt-Detail */}
      {selectedItem && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setSelectedItem(null)}
          />
          {/* Sheet */}
          <div className="fixed inset-x-0 bottom-16 z-50 bg-[#161b27] border-t border-[#2a3347] rounded-t-2xl p-5 shadow-xl">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span
                  className="w-3 h-3 rounded-full flex-shrink-0"
                  style={{ backgroundColor: CATEGORY_COLORS[selectedItem.category] ?? "#6b7280" }}
                />
                <span className="text-xs text-slate-500 uppercase tracking-wide">
                  {selectedItem.category}
                </span>
              </div>
              <button
                onClick={() => setSelectedItem(null)}
                className="text-slate-500 hover:text-slate-300 text-xl leading-none transition-colors"
              >
                &times;
              </button>
            </div>

            <h3 className="text-base font-semibold text-slate-100 mb-1">
              {selectedItem.title}
            </h3>

            <p className="text-xs text-slate-400 mb-2">
              {selectedItem.start_time
                ? `${formatDate(selectedItem.start_time)} · ${formatTime(selectedItem.start_time)}`
                : "Termin offen"}
              {selectedItem.end_time && ` – ${formatTime(selectedItem.end_time)}`}
            </p>

            {selectedItem.location && (
              <p className="text-xs text-slate-500 mb-3 flex items-center gap-1">
                <span>📍</span> {selectedItem.location}
              </p>
            )}

            {selectedItem.description && (
              <p className="text-sm text-slate-300 line-clamp-3">{selectedItem.description}</p>
            )}

            {(selectedItem.location || selectedItem.location_lat != null) && (
              <a
                href={
                  selectedItem.location
                    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedItem.location)}`
                    : `https://www.google.com/maps/search/?api=1&query=${selectedItem.location_lat},${selectedItem.location_lng}`
                }
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-block text-sky-400 text-sm hover:underline"
              >
                In Google Maps öffnen →
              </a>
            )}
          </div>
        </>
      )}
    </div>
  );
}
